// store/store.ts
import { configureStore, Middleware } from '@reduxjs/toolkit';
import { combineReducers, Action } from 'redux';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import type { PersistedState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import logger from 'redux-logger';

import blocksSlice from '../slices/blocks/blocksSlice';
import todaySessionSlice from '../slices/todaySession/todaySessionSlice';
import flashcardsReducer from '../slices/flashcards/flashcardsSlice';
import pastFlashcardsReducer from '../slices/pastFlashcards/pastFlashcardsSlice';
import vignettesReducer from '../slices/vignettes/vignettesSlice';
import statsSlice from '../slices/stats/statsSlice';
import subscriptionsReducer from '../slices/subscriptions/subscriptionsSlice';
import authReducer, {
  logout,
  readStoredImpersonation,
  sessionIdentityChanged,
} from '../slices/auth/authSlice';
import userReducer from '../slices/user/userSlice';
import examsReducer from '../slices/exams/examsSlice';
import discountReducer from '../slices/discount/discountSlice';
import notificationsReducer from '../slices/notifications/notificationsSlice';
import lecturesReducer from '../slices/lectures/lecturesSlice';
import lectureTreeReducer from '../slices/lectureTree/lectureTreeSlice';
import schoolReducer from '../slices/school/schoolSlice';
import studySlice from '../slices/study/studySlice';
import studyContextSlice from '../slices/studyContext/studyContextSlice';
import flashcardRunReducer from '../slices/flashcardRun/flashcardRunSlice';
import courseSessionReducer from '../slices/courseSession/courseSessionSlice';
import qbankProgressReducer from '../slices/qbankProgress/qbankProgressSlice';
import todayTopicsSlice from '../slices/todayTopics/todayTopicsSlice';
import videosReducer from '../slices/videos/videosSlice';
import studyHistoryReducer from '../slices/studyHistory/studyHistorySlice';
import todayStudyHistoryReducer from '../slices/todayStudyHistory/todayStudyHistorySlice';
import { flashcardCountSyncListener } from '../middleware/flashcardCountSyncListener';
import mappingJobsReducer from '../slices/mappingJobsSlice/mappingJobsSlice';
import clientUpdateReducer from '../slices/clientUpdate/clientUpdateSlice';
import assignmentsReducer from '../slices/assignments/assignmentsSlice';
import messageStateReducer from '../slices/messageState/messageStateSlice';
import chatReducer from '../slices/chat/chatSlice';
import audioOverviewsReducer from '../slices/audioOverviews/audioOverviewsSlice';
import adminStepUpReducer from '../slices/adminStepUp/adminStepUpSlice';
import { chatSessionLogoutMiddleware } from '../slices/chat/chatSessionLifecycle';
import { LecturePreview } from '../types/Lecture';

// The complete slice -> reducer map, exported so the unit-test store
// (src/test/examTestStore.ts) is built from the same input as production
// and new slices can't drift out of the test store.
export const storeReducerMap = {
  blocks: blocksSlice,
  todaySession: todaySessionSlice,
  flashcards: flashcardsReducer,
  pastFlashcards: pastFlashcardsReducer,
  vignettes: vignettesReducer,
  stats: statsSlice,
  subscription: subscriptionsReducer,
  auth: authReducer,
  user: userReducer,
  exams: examsReducer,
  lectures: lecturesReducer,
  lectureTree: lectureTreeReducer,
  school: schoolReducer,
  studyPhase: studySlice,
  studyContext: studyContextSlice,
  flashcardRun: flashcardRunReducer,
  courseSession: courseSessionReducer,
  qbankProgress: qbankProgressReducer,
  todayTopics: todayTopicsSlice,
  videos: videosReducer,
  studyHistory: studyHistoryReducer,
  todayStudyHistory: todayStudyHistoryReducer,
  mappingJobs: mappingJobsReducer,
  discount: discountReducer,
  notifications: notificationsReducer,
  clientUpdate: clientUpdateReducer,
  assignments: assignmentsReducer,
  messageState: messageStateReducer,
  chat: chatReducer,
  audioOverviews: audioOverviewsReducer,
  adminStepUp: adminStepUpReducer,
};

const appReducers = combineReducers(storeReducerMap);

// Root reducer to handle global actions like logout
const rootReducer = (
  state: ReturnType<typeof appReducers> | undefined,
  action: Action
): ReturnType<typeof appReducers> => {
  if (logout.match(action)) {
    storage.removeItem('persist:root');
    state = undefined;
  }
  // Another account's session: every user-scoped slice starts over; the
  // auth slice takes the new identity. The client-update requirement is
  // about this build, not the account, so a 426 already shown stays shown.
  if (sessionIdentityChanged.match(action) && state) {
    state = {
      auth: state.auth,
      clientUpdate: state.clientUpdate,
    } as ReturnType<typeof appReducers>;
  }
  return appReducers(state, action);
};

// Helper: normalize legacy lectures state to current entity shape without using `any`
type LecturesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
type LecturesPersistState = {
  ids: string[];
  entities: Record<string, LecturePreview>;
  status: LecturesStatus;
  error: string | null;
  selectedLectureIds: string[];
};

type EntityAdapterPersistState = {
  ids: string[];
  entities: Record<string, unknown>;
};

const isObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === 'object' && val !== null && !Array.isArray(val);

const parseMaybeJson = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  try {
    return JSON.parse(val);
  } catch {
    return undefined;
  }
};

const asStringArray = (val: unknown): string[] =>
  Array.isArray(val)
    ? (val.filter((v) => typeof v === 'string') as string[])
    : [];

const isLecturesEntityShape = (val: unknown): val is LecturesPersistState => {
  if (!isObject(val)) return false;
  const idsOk = Array.isArray(val.ids);
  const entitiesOk = isObject(val.entities);
  return idsOk && entitiesOk;
};

const isEntityAdapterShape = (
  val: unknown
): val is EntityAdapterPersistState => {
  if (!isObject(val)) return false;
  const idsOk = Array.isArray(val.ids);
  const entitiesOk = isObject(val.entities);
  return idsOk && entitiesOk;
};

const sanitizeEntityAdapterState = (
  inbound: unknown
): EntityAdapterPersistState | undefined => {
  if (!inbound) return undefined;

  const data = parseMaybeJson(inbound) ?? inbound;
  if (!isEntityAdapterShape(data)) return undefined;

  return {
    ...data,
    ids: asStringArray(data.ids),
    entities: data.entities,
  };
};

// One-time heal (persisted version < 7): strip RESIDENCY exams from the
// persisted exams slice. Backends before the ENABLE_RESIDENCY_EXAMS read-time
// filter returned users' dormant residency UserExam rows in /exams, and the
// old UI simply never rendered them — but the Sub-Internships UI does, so a
// stale rehydrated cache shows sub-I exams that fresh fetches no longer
// return. Fresh payloads are authoritative after this runs once.
export const stripResidencyExamsFromPersistedState = (
  inbound: EntityAdapterPersistState | undefined
): EntityAdapterPersistState | undefined => {
  if (!inbound) return inbound;

  const entities = isObject(inbound.entities)
    ? (inbound.entities as Record<string, unknown>)
    : undefined;

  if (!entities) return inbound;

  const isResidencyEntity = (value: unknown) =>
    isObject(value) && (value as { type?: unknown }).type === 'RESIDENCY';

  const keptEntities: Record<string, unknown> = {};
  for (const [id, entity] of Object.entries(entities)) {
    if (!isResidencyEntity(entity)) keptEntities[id] = entity;
  }

  return {
    ...inbound,
    ids: asStringArray(inbound.ids).filter((id) =>
      Object.prototype.hasOwnProperty.call(keptEntities, id)
    ),
    entities: keptEntities,
  };
};

const normalizeLecturesState = (
  inbound: unknown
): LecturesPersistState | undefined => {
  if (!inbound) return undefined;

  const data = parseMaybeJson(inbound) ?? inbound;

  // If already in the new normalized shape, ensure new fields exist
  if (isLecturesEntityShape(data)) {
    const status: LecturesStatus =
      isObject(data) &&
      typeof data.status === 'string' &&
      (['idle', 'loading', 'succeeded', 'failed'] as const).includes(
        data.status as LecturesStatus
      )
        ? (data.status as LecturesStatus)
        : 'idle';

    const selectedLectureIds = Array.isArray(
      (data as Record<string, unknown>).selectedLectureIds
    )
      ? asStringArray((data as Record<string, unknown>).selectedLectureIds)
      : typeof (data as Record<string, unknown>).selectedLectureId === 'string'
        ? [String((data as Record<string, unknown>).selectedLectureId)]
        : [];

    return {
      ids: asStringArray((data as Record<string, unknown>).ids),
      entities:
        (data as { entities: Record<string, LecturePreview> }).entities || {},
      status,
      error: ((data as Record<string, unknown>).error as string | null) ?? null,
      selectedLectureIds,
    };
  }

  // Try to reconstruct from common legacy shapes
  let lecturesArray: unknown[] | undefined;
  if (Array.isArray(data)) {
    lecturesArray = data as unknown[];
  } else if (isObject(data)) {
    if (Array.isArray(data.items)) lecturesArray = data.items as unknown[];
    else if (Array.isArray(data.list)) lecturesArray = data.list as unknown[];
    else if (Array.isArray(data.lectures))
      lecturesArray = data.lectures as unknown[];
    else if (Array.isArray(data.data)) lecturesArray = data.data as unknown[];
  }

  if (Array.isArray(lecturesArray)) {
    const ids: string[] = [];
    const entities: Record<string, LecturePreview> = {};
    for (const raw of lecturesArray) {
      if (isObject(raw) && typeof raw.id === 'string') {
        ids.push(raw.id);
        entities[raw.id] = raw as unknown as LecturePreview;
      }
    }

    const selectedId =
      isObject(data) && typeof data.selectedLectureId === 'string'
        ? data.selectedLectureId
        : isObject(data) && typeof data.selectedId === 'string'
          ? data.selectedId
          : null;

    const status: LecturesStatus =
      isObject(data) &&
      typeof data.status === 'string' &&
      (['idle', 'loading', 'succeeded', 'failed'] as const).includes(
        data.status as LecturesStatus
      )
        ? (data.status as LecturesStatus)
        : 'succeeded';

    return {
      ids,
      entities,
      status,
      error:
        (isObject(data) ? (data.error as string | null) : undefined) ?? null,
      selectedLectureIds: selectedId ? [selectedId] : [],
    };
  }

  // Unknown legacy shape — fall back to reducer initial state
  return undefined;
};

export const persistConfig = {
  key: 'root',
  storage,
  version: 7,
  // Blacklisted to not hit persist limit
  blacklist: [
    'flashcards',
    'vignettes',
    'studyHistory',
    'todayTopics',
    'qbankProgress',
    'videos',
    'mappingJobs',
    // The course module is server-authoritative and re-read after every item
    // write; a reload re-reads it rather than rehydrating a stale copy.
    'courseSession',
    // Tree nodes are server-authoritative and potentially large. Expansion and
    // hidden-row visibility remain in memory with the rest of this slice.
    'lectureTree',
    'clientUpdate',
    // Assignment statuses are time-sensitive; always refetch rather than
    // rehydrate stale data.
    'assignments',
    // Today's counters gate the free-plan daily limits and carry no date
    // history, so a persisted copy silently gates the user against another
    // day's totals. Always refetch instead (ORA-319).
    'todayStudyHistory',
    // Chat turns are live stream state (partial text, in-flight turns, block
    // payloads). Rehydrating them would resurrect a half-streamed turn that
    // can never finish, and conversations are re-read from the server anyway.
    'chat',
    // Audio Overview statuses and plan state are server-owned and change
    // while the app is closed; always refetch (ORA-735).
    'audioOverviews',
    // The admin step-up prompt is transient UI tied to in-flight requests; a
    // rehydrated "open" prompt would have nothing waiting on it (ORA-864).
    'adminStepUp',
  ],
  migrate: async (inboundState: PersistedState): Promise<PersistedState> => {
    try {
      if (!inboundState) return inboundState;

      if (!isObject(inboundState)) return inboundState;

      // Clone shallowly to avoid mutating the original inbound state
      const nextState: Record<string, unknown> = { ...inboundState };

      // Drop large session-content slices from persisted state.
      delete nextState.flashcards;
      delete nextState.vignettes;
      delete nextState.videos;

      // Blacklisting only stops future writes, so drop any copy written before
      // `todayStudyHistory` became ephemeral (ORA-319).
      delete nextState.todayStudyHistory;

      // Entity adapter selectors assume `{ ids, entities }`. Older or partially
      // persisted mobile WebView state can miss `ids`, crashing on `ids.map`.
      (['blocks', 'exams'] as const).forEach((sliceKey) => {
        const sanitizedState = sanitizeEntityAdapterState(
          (inboundState as Record<string, unknown>)[sliceKey]
        );

        if (sanitizedState === undefined) {
          delete nextState[sliceKey];
        } else {
          nextState[sliceKey] = sanitizedState;
        }
      });

      // v7: strip stale RESIDENCY exams cached before the backend read-time
      // filter shipped (see stripResidencyExamsFromPersistedState).
      const persistedVersion =
        (inboundState as { _persist?: { version?: number } })._persist
          ?.version ?? -1;

      if (persistedVersion < 7) {
        nextState.exams = stripResidencyExamsFromPersistedState(
          nextState.exams as EntityAdapterPersistState | undefined
        );

        if (nextState.exams === undefined) delete nextState.exams;
      }

      // Normalize lectures slice into the new entity shape
      const inboundLectures = (inboundState as Record<string, unknown>)
        ?.lectures;

      const normalizedLectures = normalizeLecturesState(inboundLectures);
      if (normalizedLectures === undefined) {
        // Let reducer initialize with its own initial state by omitting the key
        delete nextState.lectures;
      } else {
        nextState.lectures = normalizedLectures;
      }

      // A hydrated flashcard run receives a fresh instance id on bootstrap.
      // Older persisted runs did not have this field, so make their stale
      // instance explicitly unscoped until the next bootstrap replaces them.
      // Likewise the per-card scheduling map (ORA-1090): a run persisted
      // before it existed rehydrates with an empty map, so the reducers can
      // write into it until the next bootstrap replaces the run.
      const persistedFlashcardRun = parseMaybeJson(nextState.flashcardRun);
      if (isObject(persistedFlashcardRun)) {
        nextState.flashcardRun = {
          ...persistedFlashcardRun,
          hydrationRunId:
            typeof persistedFlashcardRun.hydrationRunId === 'string'
              ? persistedFlashcardRun.hydrationRunId
              : null,
          schedulingMetaById: isObject(persistedFlashcardRun.schedulingMetaById)
            ? persistedFlashcardRun.schedulingMetaById
            : {},
        };
      }

      // Sessions are cookies now (ORA-987): drop the legacy JWTs persisted
      // auth may still carry (the boot exchange reads its own copy). The
      // impersonation record lives in its own localStorage key (ORA-808);
      // persisted `auth` may lag behind it (persist writes are throttled), so
      // the key wins on every rehydrate, or an admin mid-impersonation would
      // lose the Stop Impersonating control.
      if (isObject(nextState.auth)) {
        const auth: Record<string, unknown> = {
          ...nextState.auth,
          impersonatedBy: readStoredImpersonation()?.impersonatedBy ?? null,
        };

        delete auth.authToken;
        delete auth.refreshToken;
        delete auth.originalAdminSession;

        // No sign-in survives a page load. A persisted 'loading' (a reload
        // before the throttled persist wrote the signed-in state) would
        // otherwise hold ProtectedRoute's spinner forever.
        if (auth.status === 'loading') auth.status = 'idle';

        nextState.auth = auth;
      }

      return nextState as PersistedState;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Persist migration failed, using inbound state:', e);
      }
      return inboundState;
    }
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(chatSessionLogoutMiddleware)
      // Cast: its addListener dispatch type would change AppDispatch.
      .concat(flashcardCountSyncListener.middleware as Middleware);

    if (process.env.NODE_ENV !== 'production') {
      return defaultMiddleware.concat(logger);
    }

    return defaultMiddleware;
  },
});

export const persistor = persistStore(store);

const persistRootKey = 'persist:root';

const getByteSize = (value: string): number => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  return value.length;
};

type PersistRootRecord = Record<string, string>;

const parsePersistRoot = (persistedValue: string): PersistRootRecord | null => {
  try {
    const parsed = JSON.parse(persistedValue) as unknown;
    if (!isObject(parsed)) {
      return null;
    }
    return parsed as PersistRootRecord;
  } catch {
    return null;
  }
};

const logPersistStorageSizes = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  const persistedValue = localStorage.getItem(persistRootKey);
  if (!persistedValue) {
    console.info('[persist-diagnostics] No persisted root state found.');
    return;
  }

  const parsed = parsePersistRoot(persistedValue);
  if (!parsed) {
    console.warn('[persist-diagnostics] Unable to parse persisted root state.');
    return;
  }

  const entries = Object.entries(parsed).map(([sliceKey, sliceValue]) => ({
    sliceKey,
    bytes: getByteSize(sliceValue),
  }));

  entries.sort((a, b) => b.bytes - a.bytes);

  const totalBytes = getByteSize(persistedValue);
  const totalKilobytes = Number((totalBytes / 1024).toFixed(1));

  console.groupCollapsed(
    `[persist-diagnostics] total=${totalKilobytes}KB (${totalBytes} bytes)`
  );

  entries.forEach(({ sliceKey, bytes }) => {
    const kb = Number((bytes / 1024).toFixed(1));
    console.log(`${sliceKey}: ${kb}KB (${bytes} bytes)`);
  });

  try {
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota ?? 0;
      const usage = estimate.usage ?? 0;

      const usagePercent =
        quota > 0 ? ((usage / quota) * 100).toFixed(1) : null;

      console.log(
        `storage estimate: usage=${usage} quota=${quota}${usagePercent ? ` (${usagePercent}%)` : ''}`
      );
    }
  } catch {
    // Ignore diagnostics-only storage estimate errors.
  }

  console.groupEnd();
};

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (
    window as Window & {
      logPersistStorageSizes?: () => Promise<void>;
    }
  ).logPersistStorageSizes = logPersistStorageSizes;
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
